import {
  type AnyFieldApi,
  type AnyFieldMetaBase,
  useStore,
} from '@tanstack/react-form';
import { useEffect } from 'react';

import { FORM_MESSAGES } from '~/common/constants';
import { useSelectedAlert } from '~/stores/navigationStore';

import { CLASSIFICATION } from './constants';
import {
  type ClassifyFieldValue,
  type ClassifyFormData,
  type SelectOption,
  withForm,
} from './hooks/ClassifyFormHook';
import useDocumentCategoryOptions from './hooks/useDocumentCategoryOptions';
import useDocumentTypeOptions from './hooks/useDocumentTypeOptions';
import { UNCLASSIFIED_WARNING } from './language.json';

/**
 * Validates the document category field.
 * Accepts the broader union type that TypeScript infers from the
 * `ClassifyFormData` index signature; arrays are treated as unset.
 */
function validateDocumentCategory(
  value: ClassifyFieldValue,
  fieldApi: AnyFieldApi,
): string | undefined {
  if (!value || Array.isArray(value)) return FORM_MESSAGES.requiredField;
  const docType = fieldApi.form.getFieldValue(
    'documentType',
  ) as SelectOption | null;
  if (
    value.value === CLASSIFICATION.unclassified &&
    docType?.value !== CLASSIFICATION.notListed
  ) {
    return UNCLASSIFIED_WARNING;
  }
  return undefined;
}

const ClassifyStaticFormContent = withForm({
  defaultValues: {} as ClassifyFormData,
  render: function ClassifyStaticFormContent({ form }) {
    const selectedDocument = useSelectedAlert();
    const selectedDocumentCategoryValue =
      selectedDocument.additionalReferences.DocumentCategory;
    const selectedDocumentTypeValue =
      +selectedDocument.additionalReferences.DocumentTypeId;

    const selectedDocumentCategory = useStore(
      form.store,
      (value) => value.values.documentCategory,
    );

    const documentCategoryOptions = useDocumentCategoryOptions();
    const documentTypeOptions = useDocumentTypeOptions(
      selectedDocumentCategory ?? {
        value: selectedDocumentCategoryValue,
      },
    );

    const clearStaticFieldErrors = () => {
      form.setFieldMeta(
        CLASSIFICATION.docCategory,
        (prev: AnyFieldMetaBase) => ({ ...prev, errorMap: {} }),
      );
      form.setFieldMeta(CLASSIFICATION.docType, (prev: AnyFieldMetaBase) => ({
        ...prev,
        errorMap: {},
      }));
    };

    // Initialise dropdowns when a document is selected
    useEffect(() => {
      form.setFieldValue(CLASSIFICATION.docType, null);
      clearStaticFieldErrors();

      const currentCategory = form.getFieldValue(
        CLASSIFICATION.docCategory,
      ) as SelectOption;

      if (currentCategory?.value !== selectedDocumentCategoryValue) {
        form.setFieldValue(CLASSIFICATION.docCategory, {
          label: selectedDocumentCategoryValue,
          value: selectedDocumentCategoryValue,
        });
      }
      // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [selectedDocument]);

    // Hydrate document type once options for the selected category are available.
    useEffect(() => {
      if (selectedDocumentCategory?.value !== selectedDocumentCategoryValue) {
        return;
      }

      const docTypeOption = documentTypeOptions.find(
        (x) => x.value === selectedDocumentTypeValue,
      );
      const currentType = form.getFieldValue(
        CLASSIFICATION.docType,
      ) as SelectOption;

      if (docTypeOption && currentType?.value !== docTypeOption.value) {
        form.setFieldValue(CLASSIFICATION.docType, docTypeOption);
      }
      // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [
      selectedDocumentCategory,
      selectedDocumentCategoryValue,
      selectedDocumentTypeValue,
      documentTypeOptions,
    ]);

    return (
      <>
        <form.AppField
          name="documentCategory"
          validators={{
            onSubmit: ({
              value,
              fieldApi,
            }: {
              value: ClassifyFieldValue;
              fieldApi: AnyFieldApi;
            }) => validateDocumentCategory(value, fieldApi),
          }}
          defaultValue={null}
        >
          {(field) => (
            <field.SelectField
              id={CLASSIFICATION.docCategory}
              label="Select Document Category"
              options={documentCategoryOptions}
              onChange={() => {
                form.setFieldValue('documentType', null);
                clearStaticFieldErrors();
              }}
              sx={{ mb: 4 }}
              data-tracking={CLASSIFICATION.docCategory}
              data-testid={CLASSIFICATION.docCategory}
            />
          )}
        </form.AppField>

        <form.AppField
          name="documentType"
          validators={{
            onSubmit: ({ value }: { value: ClassifyFieldValue }) =>
              value && !Array.isArray(value)
                ? undefined
                : FORM_MESSAGES.requiredField,
          }}
          defaultValue={null}
        >
          {(field) => (
            <field.SelectField
              id={CLASSIFICATION.docType}
              label="Select Document Type"
              options={documentTypeOptions}
              sx={{ mb: 4 }}
              data-tracking={CLASSIFICATION.docType}
              data-testid={CLASSIFICATION.docType}
            />
          )}
        </form.AppField>
      </>
    );
  },
});

export default ClassifyStaticFormContent;
